import {
  ErrorMessage,
  Flex,
  HintIcon,
  Radio,
  type RadioGroupProps,
  Space,
} from '@/components';
import type { ReactNode } from 'react';
import {
  Controller,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { Form } from '.';
import { FieldLabel, type FieldLabelSize } from './FieldLabel';
import { RequiredTag } from './RequiredTag';
import type { FormControlProp } from './types';

type Props<T extends FieldValues> = {
  name: Path<T>;
  label?: string;
  hint?: string;
  control: FormControlProp<T>;
  options?: Array<{
    label: string | ReactNode;
    value: string | number | boolean;
    disabled?: boolean;
    hint?: string | ReactNode;
  }>;
  errors: FieldErrors;
  required?: boolean;
  optionType?: 'default' | 'button';
  buttonStyle?: 'outline' | 'solid';
  disabled?: boolean;
  marginBottom?: number;
  shouldCoexistOnChange?: boolean;
  labelSize?: FieldLabelSize;
  isShowFieldLabelWhenEmpty?: boolean;
  hiddenErrorMessage?: boolean;
  optionsDirection?: 'vertical' | 'horizontal';
} & Pick<RadioGroupProps, 'onChange'>;

export const RadioGroupField = <T extends FieldValues>({
  name,
  label,
  hint,
  control,
  options,
  errors,
  required,
  optionType = 'default',
  buttonStyle = 'outline',
  disabled,
  marginBottom,
  onChange,
  shouldCoexistOnChange = false,
  labelSize = 'medium',
  isShowFieldLabelWhenEmpty = false,
  hiddenErrorMessage = false,
  optionsDirection = 'horizontal',
}: Props<T>) => {
  return (
    <Form.Item
      label={
        (label || isShowFieldLabelWhenEmpty) && (
          <FieldLabel
            text={label ?? ''}
            hint={hint}
            required={required}
            size={labelSize}
          />
        )
      }
      style={{ marginBottom }}
    >
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const { onChange: fieldOnChange, ...restField } = field;
          return (
            <Radio.Group
              {...restField}
              optionType={optionType}
              buttonStyle={buttonStyle}
              disabled={disabled}
              style={{ display: 'block' }}
              onChange={(e) => {
                if (shouldCoexistOnChange || !onChange) {
                  fieldOnChange(e);
                }
                onChange?.(e);
              }}
              data-dd-privacy="allow"
            >
              <Space>
                {!label && required && <RequiredTag />}

                <Flex
                  vertical={optionsDirection === 'vertical'}
                  gap={optionsDirection === 'vertical' ? 8 : 0}
                >
                  {options?.map(({ value, label, disabled, hint }) => (
                    <Radio
                      key={`${JSON.stringify(name)}.${value.toString()}`}
                      value={value}
                      disabled={disabled}
                    >
                      {hint ? (
                        <Flex gap={4} align="center">
                          {label}
                          <HintIcon hint={hint} />
                        </Flex>
                      ) : (
                        label
                      )}
                    </Radio>
                  ))}
                </Flex>
              </Space>
            </Radio.Group>
          );
        }}
      />
      {!hiddenErrorMessage && <ErrorMessage errors={errors} name={name} />}
    </Form.Item>
  );
};
